import { makeStyles } from "@mui/styles";
import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  TooltipProps,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { WhiteOnBlackTooltip } from "./CustomTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    color: "#ffffff",
    boxSizing: "border-box",
    transitionProperty: "transform, color, border-color, background-color",
    transitionDuration: "150ms",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
    "&:disabled": {
      backgroundColor: theme.palette.divider,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: theme.palette.neutral.dark,
      },
    },
  },
  zoom: {
    "&:hover": {
      transform: "scale(1.02)",
    },
    "&:active": {
      transform: "scale(0.95)",
      transitionDuration: "32ms",
      transitionTimingFunction: "ease-in-out",
    },
  },
  primary: {
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  flush: {
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondary: {
    backgroundColor: theme.palette.secondary.main,
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  error: {
    backgroundColor: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  info: {
    backgroundColor: theme.palette.info.main,
    "&:hover": {
      backgroundColor: theme.palette.info.dark,
    },
  },
  success: {
    backgroundColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  warning: {
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  neutral: {
    backgroundColor: theme.palette.neutral.main,
    "&:hover": {
      backgroundColor: theme.palette.neutral.dark,
    },
  },
}));

export type ColorOptions =
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning"
  | "neutral"
  | "flush";

export interface IconButtonContainedProps extends Omit<MuiIconButtonProps, "color"> {
  zoom?: boolean;
  color?: ColorOptions;
  hover?: React.ReactNode;
  tooltipLabel?: string | JSX.Element;
  tooltipPlacement?: string;
}

export const IconButtonContained = (props: IconButtonContainedProps) => {
  const classes = useStyles();
  const [hover, setHover] = useState(false);
  const MuiProps = { ...props };
  delete MuiProps["color"];
  delete MuiProps["zoom"];
  delete MuiProps["hover"];
  delete MuiProps["children"];
  delete MuiProps["tooltipLabel"];
  delete MuiProps["tooltipPlacement"];

  const button = (
    <MuiIconButton
      aria-label={typeof props.tooltipLabel === "string" ? props.tooltipLabel : ""}
      {...MuiProps}
      className={`${props.className} ${classes.root} ${
        classes[props.color ?? "primary"]
      } ${props.zoom && classes.zoom}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {hover ? (props.hover ? props.hover : props.children) : props.children}
    </MuiIconButton>
  );

  return props.tooltipLabel ? (
    <WhiteOnBlackTooltip
      arrow
      title={<Typography variant="body1">{props.tooltipLabel ?? ""}</Typography>}
      placement={props.tooltipPlacement as keyof TooltipProps["placement"]}>
      <div style={{ display: "inline-block" }}>{button}</div>
    </WhiteOnBlackTooltip>
  ) : (
    button
  );
};
